import React from 'react';
import {observer} from 'mobx-react';
import {Container, TextField, Typography} from '@mui/material';
import {DarkButton, InlineButton} from './buttons';
import {FortressDivider} from './fortress-divider';
import OfficersList from './corporate/officers';
import RenewalStore from '../stores/renewal-store';
import editButtonAction from '../ui/renewal/renewal-edit-button-action';
import {formatAddressLocality, getAddressCountry, getCountryOption} from '../assets/address-details';
import {hasActiveOfficer, registeredAgentName} from '../assets/company-review';

/** The most officers a filing lists; officers marked for removal do not count. */
const maxOfficers = 6;

export const CompanyDetailsEditor = observer(({companyInfo, generalPartnersAddressOnly = false, registeredAgentLocked = false, selectAgentType = false}: {
    companyInfo: any;
    generalPartnersAddressOnly?: boolean;
    registeredAgentLocked?: boolean;
    selectAgentType?: boolean;
}) => <>
            <FortressDivider textAlign="left" sx={{mb: 1}}>Company Details</FortressDivider>

            <Container disableGutters sx={{mb: 3}}>
                <TextField disabled fullWidth label="Company Name" variant="outlined" value={companyInfo.name}
                           sx={{color: 'black'}}/>
            </Container>

            <Container disableGutters sx={{mb: 3}}>
                <TextField disabled fullWidth label={
                    <>
                        <span>FEI/EIN Number</span>
                        <InlineButton onClick={() => editButtonAction({field: "ein"})}>Change</InlineButton>
                    </>
                } variant="outlined" placeholder="Please Adjust EIN" value={RenewalStore.getUpdatedCompanyInfo.fein ?? "Not Applicable"}
                           sx={{color: 'black'}}/>
            </Container>

            <FortressDivider textAlign="left">Address Details</FortressDivider>

            <Container disableGutters sx={{mb: 3, display: 'flex'}}>
                <Container disableGutters sx={{pr: {xs: 1, md: 3}}}>
                    <TextField disabled fullWidth
                               label={<Container disableGutters sx={{display: 'flex', justifyContent: 'space-between'}}>
                                   <span>Principal Address</span>
                                   <InlineButton
                                       onClick={() => editButtonAction({field: "principal"})}>Change</InlineButton>
                               </Container>} margin="normal" variant="outlined"
                               value={`${RenewalStore.getUpdatedCompanyInfo.principalAddress.street1}${RenewalStore.getUpdatedCompanyInfo.principalAddress.street2 ? `, ${RenewalStore.getUpdatedCompanyInfo.principalAddress.street2}` : ''}`}/>
                    <TextField disabled fullWidth variant="outlined"
                               value={formatAddressLocality(RenewalStore.getUpdatedCompanyInfo.principalAddress)}/>
                    <TextField disabled fullWidth label="Country" margin="normal"
                               value={getCountryOption(getAddressCountry(RenewalStore.getUpdatedCompanyInfo.principalAddress)).label}/>
                </Container>
                <Container disableGutters sx={{pl: {xs: 1, md: 3}}}>
                    <TextField disabled fullWidth label={
                        <>
                            <span>Mailing Address</span>
                            <InlineButton onClick={() => editButtonAction({field: "mailing"})}>Change</InlineButton>
                        </>
                    } margin="normal" variant="outlined"
                               value={`${RenewalStore.getUpdatedCompanyInfo.mailingAddress.street1}${RenewalStore.getUpdatedCompanyInfo.mailingAddress.street2 ? `, ${RenewalStore.getUpdatedCompanyInfo.mailingAddress.street2}` : ''}`}/>
                    <TextField disabled fullWidth variant="outlined"
                               value={formatAddressLocality(RenewalStore.getUpdatedCompanyInfo.mailingAddress)}/>
                    <TextField disabled fullWidth label="Country" margin="normal"
                               value={getCountryOption(getAddressCountry(RenewalStore.getUpdatedCompanyInfo.mailingAddress)).label}/>
                </Container>
            </Container>

            <FortressDivider textAlign="left">Registered Agent Details</FortressDivider>

            <Container disableGutters sx={{mb: 3, display: 'flex'}}>
                <Container disableGutters sx={{}}>
                    <TextField disabled fullWidth
                               label={<Container disableGutters sx={{display: 'flex', justifyContent: 'space-between'}}>
                                   <span>Registered Agent</span>
                                   {!registeredAgentLocked && <InlineButton
                                       onClick={() => editButtonAction({field: "registered_agent", selectAgentType})}>Change</InlineButton>}
                               </Container>} margin="normal" variant="outlined"
                               value={registeredAgentName(RenewalStore.getUpdatedCompanyInfo.registeredAgent)}/>
                    <TextField disabled fullWidth variant="outlined"
                               value={`${RenewalStore.getUpdatedCompanyInfo.registeredAgent.address}`}/>
                    <TextField disabled fullWidth margin='dense' variant="outlined"
                               value={`${RenewalStore.getUpdatedCompanyInfo.registeredAgent.city}, ${RenewalStore.getUpdatedCompanyInfo.registeredAgent.state} ${RenewalStore.getUpdatedCompanyInfo.registeredAgent.zipCode}`}/>
                </Container>
            </Container>

            <FortressDivider textAlign="left" sx={{mb: 2}}>Officer/Director Details</FortressDivider>

            {!hasActiveOfficer(RenewalStore.getUpdatedCompanyInfo.officers) && <Typography color="error" sx={{mb: 2}}>
                No officers are included in this filing. {generalPartnersAddressOnly ? 'Please contact support to continue.' : 'Add at least one officer or principal to continue.'}
            </Typography>}
            <OfficersList officers={companyInfo.officers} addressOnly={generalPartnersAddressOnly}/>
            {!generalPartnersAddressOnly && RenewalStore.getUpdatedCompanyInfo.officers.filter(officer => officer.remove !== true).length < maxOfficers
                ? <DarkButton onClick={() => editButtonAction({field:"officer", index:RenewalStore.getUpdatedCompanyInfo.officers.length})}>Add New Officer</DarkButton> : null}
</>);
